import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';
import Image from '../../../components/AppImage';
import Input from '../../../components/ui/Input';
import Button from '../../../components/ui/Button';

const ProfileInformationTab = ({ user, onUpdateProfile }) => {
  const [formData, setFormData] = useState({
    name: user?.name || '',
    whatsappNumber: user?.mobile || '',
    email: user?.email || '',
    businessName: user?.vendorDetails?.businessName || '',
    businessAddress: user?.address || '',
    profilePhoto: user?.profilePhoto || ''
  });
  const [errors, setErrors] = useState({});
  const [isLoading, setIsLoading] = useState(false);
  const [photoPreview, setPhotoPreview] = useState(user?.profilePhoto || '');
  const [showWhatsAppVerification, setShowWhatsAppVerification] = useState(false);
  const [verificationCode, setVerificationCode] = useState('');

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
    if (errors[name]) {
      setErrors(prev => ({
        ...prev,
        [name]: ''
      }));
    }
  };

  const handlePhotoUpload = (e) => {
    const file = e.target.files[0];
    if (file) {
      if (file.size > 5 * 1024 * 1024) {
        setErrors(prev => ({
          ...prev,
          profilePhoto: 'File size must be less than 5MB'
        }));
        return;
      }
      const reader = new FileReader();
      reader.onload = (e) => {
        const imageUrl = e.target.result;
        setPhotoPreview(imageUrl);
        setFormData(prev => ({
          ...prev,
          profilePhoto: imageUrl
        }));
      };
      reader.readAsDataURL(file);
    }
  };

  const validateForm = () => {
    const newErrors = {};
    if (!formData.name.trim()) newErrors.name = 'Name is required';
    if (!formData.email.trim()) newErrors.email = 'Email is required';
    else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email))
      newErrors.email = 'Please enter a valid email address';
    if (user?.role === 'vendor' && !formData.businessName.trim())
      newErrors.businessName = 'Business name is required for vendors';
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleWhatsAppVerification = async () => {
    setIsLoading(true);
    setTimeout(() => {
      setShowWhatsAppVerification(true);
      setIsLoading(false);
    }, 1000);
  };

  const handleVerifyCode = async () => {
    if (verificationCode === '123456') {
      setShowWhatsAppVerification(false);
      setVerificationCode('');
      handleSubmit();
    } else {
      setErrors(prev => ({
        ...prev,
        verificationCode: 'Invalid verification code. Use 123456 for demo.'
      }));
    }
  };

  const handleSubmit = async (e) => {
    if (e) e.preventDefault();
    if (!validateForm()) return;
    if (formData.whatsappNumber !== user?.whatsappNumber && !showWhatsAppVerification) {
      handleWhatsAppVerification();
      return;
    }
    setIsLoading(true);
    try {
      setTimeout(() => {
        onUpdateProfile(formData);
        setIsLoading(false);
      }, 1500);
    } catch (error) {
      setErrors({ submit: 'Failed to update profile. Please try again.' });
      setIsLoading(false);
    }
  };

  const getRoleDisplayName = (role) => {
    switch (role) {
      case 'admin': return 'Administrator';
      case 'vendor': return 'Vendor Manager';
      case 'employee': return 'Employee';
      default: return 'User';
    }
  };

  // Helper to check expiry
  const isSubscriptionExpired = (endDate) => {
    if (!endDate) return false; // treat null as ongoing (like trial)
    return new Date(endDate) < new Date();
  };

  return (
    <div className="space-y-6">
      {/* Profile Photo Section */}
      <div className="bg-card border border-border rounded-lg p-6">
        <h3 className="text-lg font-semibold text-foreground mb-4">Profile</h3>
      </div>

      {/* Basic Information */}
      <div className="bg-card border border-border rounded-lg p-6">
        <h3 className="text-lg font-semibold text-foreground mb-4">Basic Information</h3>
        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <Input
              label="Full Name"
              name="name"
              type="text"
              value={formData.name}
              onChange={handleInputChange}
              error={errors.name}
              required
              placeholder="Enter your full name"
              disabled
            />
            <Input
              label="User Role"
              type="text"
              value={getRoleDisplayName(user?.role)}
              disabled
              description="Contact administrator to change role"
            />
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <Input
              label="Email Address"
              name="email"
              type="email"
              value={formData.email}
              onChange={handleInputChange}
              error={errors.email}
              required
              disabled
              placeholder="Enter your email address"
            />
            <Input
              label="WhatsApp Number"
              name="whatsappNumber"
              type="tel"
              value={formData.whatsappNumber}
              onChange={handleInputChange}
              error={errors.whatsappNumber}
              required
              disabled
              placeholder="+91 90000 00000"
              description="Used for OTP verification"
            />
          </div>

          {/* Business Information for Vendors */}
          {user?.role === 'vendor' && (
            <>
              <div className="border-t border-border pt-4 mt-6">
                <h4 className="text-md font-medium text-foreground mb-4">Business Information</h4>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <Input
                    label="Business Name"
                    name="businessName"
                    type="text"
                    value={formData.businessName}
                    onChange={handleInputChange}
                    error={errors.businessName}
                    required
                    placeholder="Enter business name"
                    disabled
                  />
                  <Input
                    label="Business Address"
                    name="businessAddress"
                    type="text"
                    value={formData.businessAddress}
                    onChange={handleInputChange}
                    placeholder="Enter business address"
                    disabled
                  />
                </div>
              </div>

              {/* Subscription Details */}
              {user?.vendorDetails?.subscription && (
                <div className="border-t border-border pt-4 mt-6">
                  <h4 className="text-md font-medium text-foreground mb-4">Subscription Details</h4>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <Input
                      label="Plan"
                      type="text"
                      value={user.vendorDetails.subscription.plan || 'N/A'}
                      disabled
                    />
                    <div>
                      <Input
                        label="Status"
                        type="text"
                        value={user.vendorDetails.subscription.status || 'N/A'}
                        disabled
                      />
                      <p
                        className={`text-sm font-medium mt-1 ${
                          isSubscriptionExpired(user.vendorDetails.subscription.endDate)
                            ? 'text-destructive'
                            : 'text-success'
                        }`}
                      >
                        {isSubscriptionExpired(user.vendorDetails.subscription.endDate)
                          ? 'Expired'
                          : 'Active'}
                      </p>
                    </div>
                    <Input
                      label="Payment Method"
                      type="text"
                      value={user.vendorDetails.subscription.paymentMethod || 'N/A'}
                      disabled
                    />
                    <Input
                      label="Amount"
                      type="text"
                      value={`₹ ${user.vendorDetails.subscription.amount || 0}`}
                      disabled
                    />
                    <Input
                      label="Start Date"
                      type="text"
                      value={
                        user.vendorDetails.subscription.startDate
                          ? new Date(user.vendorDetails.subscription.startDate).toLocaleDateString()
                          : 'N/A'
                      }
                      disabled
                    />
                    <Input
                      label="End Date"
                      type="text"
                      value={
                        user.vendorDetails.subscription.endDate
                          ? new Date(user.vendorDetails.subscription.endDate).toLocaleDateString()
                          : 'Not Set'
                      }
                      disabled
                    />
                  </div>
                </div>
              )}
            </>
          )}

          {errors.submit && (
            <div className="bg-destructive/10 border border-destructive/20 rounded-md p-3">
              <p className="text-sm text-destructive">{errors.submit}</p>
            </div>
          )}
        </form>
      </div>

      {/* WhatsApp Verification Modal */}
      {showWhatsAppVerification && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-1200">
          <div className="bg-card border border-border rounded-lg p-6 w-full max-w-md mx-4">
            <div className="text-center mb-4">
              <div className="w-12 h-12 bg-success/10 rounded-full flex items-center justify-center mx-auto mb-3">
                <Icon name="MessageCircle" size={24} className="text-success" />
              </div>
              <h3 className="text-lg font-semibold text-foreground">Verify WhatsApp Number</h3>
              <p className="text-sm text-muted-foreground mt-1">
                We've sent a verification code to {formData.whatsappNumber}
              </p>
            </div>
            <div className="space-y-4">
              <Input
                label="Verification Code"
                type="text"
                value={verificationCode}
                onChange={(e) => setVerificationCode(e.target.value)}
                error={errors.verificationCode}
                placeholder="Enter 6-digit code"
                description="Demo code: 123456"
              />
              <div className="flex space-x-3">
                <Button
                  variant="outline"
                  fullWidth
                  onClick={() => {
                    setShowWhatsAppVerification(false);
                    setVerificationCode('');
                    setErrors({});
                  }}
                >
                  Cancel
                </Button>
                <Button
                  fullWidth
                  onClick={handleVerifyCode}
                  disabled={!verificationCode}
                >
                  Verify
                </Button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ProfileInformationTab;
